/**
 * The room behind the doors.
 *
 * What was here before was a row of panels with a small arc drawn under each
 * one. Every fact on it was true and the shape said nothing: two projects
 * produced two floating cards, and nobody looking at it saw a command table.
 * A bridge is a *table with chairs around it* — that is the whole read — so the
 * table is now the dominant object in the room and everything else is arranged
 * around it.
 *
 * Three rules govern this file, and they are what separate it from set
 * dressing:
 *
 * 1. Nothing here is invented to complete the picture. Every number on the
 *    table and every word on a station is the same fact the list view shows,
 *    with the same provenance and the same freshness.
 * 2. A chair is furniture; a *person* in a chair is a claim. Chairs are drawn
 *    whether or not anybody holds them — a command table with two occupied
 *    seats and no other chairs is not a command table — but a figure appears
 *    only where a lease is genuinely valid right now. `lib/seat.ts` decides
 *    that, and only that module gets to.
 * 3. The room is a layout, not a second source of truth. It renders the
 *    portfolio projection it is handed; the intervention strip and the crew
 *    section below it continue unchanged, so nothing is only reachable
 *    through the scenery.
 *
 * The geometry is a pair of arcs — the far side of the table faces you, the
 * near side is seen from behind — built from per-seat `--c` (how central a
 * seat is) driving a translate and a scale. No layout is read to draw it.
 */
import { Link } from 'react-router-dom';
import type { InterventionItem, PortfolioView, ProjectTile } from '@dcc/shared';
import { FreshnessTag, Meter, StateChip, Unknown } from './Primitives';
import { TONE_GLYPH, toneFor } from '../lib/tone';
import { readSeat, type SeatReading } from '../lib/seat';

/**
 * How many places the table is laid for.
 *
 * A table is a table because it has more places than people at it, and because
 * it has two sides. The far side — the one facing you, where a console is
 * legible — carries the projects, and is laid for at least five whatever the
 * portfolio holds. The near side is the foreground: three chairs seen from
 * behind, always furniture.
 *
 * So the smallest table is eight places, which is what two connected projects
 * sit at instead of hovering as two aligned cards, and what a portfolio with
 * no projects at all still looks like.
 *
 * The spare places carry no project, no workstream and no invented state.
 * They say they are unassigned, which is what they are.
 */
const MIN_FAR_PLACES = 5;
const NEAR_PLACES = 3;

/**
 * How central a seat is within its arc, 0 at the ends and 1 in the middle.
 * This is the only geometry input: the far arc pushes its centre away and the
 * near arc pulls its centre forward, which is what makes one ellipse out of
 * two rows.
 */
function centreness(index: number, count: number): number {
  if (count <= 1) return 1;
  const middle = (count - 1) / 2;
  return 1 - Math.abs(index - middle) / middle;
}

/**
 * A chair.
 *
 * Two drawings, because a table has two sides and drawing both the same way is
 * exactly what stopped the old room reading as one. `toward` is the far side:
 * you see the front of the chair, its backrest behind the occupant, its arms
 * and its seat. `away` is the near side: you see the back of the backrest and,
 * when the seat is held, the back of a head above it.
 *
 * Deliberately anonymous. A head, shoulders, a backrest and a pedestal — no
 * insignia, no uniform, no likeness.
 */
function Chair({ facing, occupied }: { facing: 'toward' | 'away'; occupied: boolean }) {
  return (
    <svg
      viewBox="0 0 64 74"
      aria-hidden="true"
      focusable="false"
      className="chair"
      data-facing={facing}
    >
      {/* The pedestal and foot are the same from either side. */}
      <ellipse className="chair__foot" cx="32" cy="68" rx="15" ry="4.5" />
      <rect className="chair__stem" x="28.5" y="48" width="7" height="18" rx="3" />

      {facing === 'toward' ? (
        <>
          {/* Backrest first: it sits behind whoever is in the chair. */}
          <rect className="chair__back" x="15" y="5" width="34" height="36" rx="11" />
          {occupied && (
            <>
              <path className="chair__body" d="M17 44 v-6 a15 15 0 0 1 30 0 v6 Z" />
              <circle className="chair__head" cx="32" cy="21" r="8" />
            </>
          )}
          {/* Arms and seat close over the front, so the figure sits *in* it. */}
          <rect className="chair__arm" x="8" y="34" width="9" height="8" rx="4" />
          <rect className="chair__arm" x="47" y="34" width="9" height="8" rx="4" />
          <rect className="chair__seat" x="13" y="41" width="38" height="8" rx="4" />
        </>
      ) : (
        <>
          {/* Seen from behind, the head clears the top of the backrest and the
              backrest hides the rest — which is the whole reason this side
              reads as a person sitting at a table facing away from you. */}
          {occupied && <circle className="chair__head" cx="32" cy="11" r="7.5" />}
          <rect
            className="chair__back chair__back--rear"
            x="13"
            y="15"
            width="38"
            height="33"
            rx="11"
          />
          <path className="chair__seam" d="M32 19 V45" />
          <rect className="chair__seat" x="11" y="44" width="42" height="7" rx="3.5" />
        </>
      )}
    </svg>
  );
}

/**
 * The label under a chair.
 *
 * An empty chair with nothing written on it is the thing that made the old
 * room unreadable: it could equally mean nobody has claimed this, the claim is
 * still being made, or a lease died and nobody noticed. Every one of those now
 * says which it is, and what it would take to sit down.
 */
function SeatLabel({ seat }: { seat: SeatReading }) {
  return (
    <div className="seat__label">
      <span className="seat__headline">{seat.headline}</span>
      {seat.workstream && <span className="seat__workstream">{seat.workstream}</span>}
      <span className="seat__availability">{seat.expiry ?? seat.availability}</span>
    </div>
  );
}

/** A place at the table that carries a project. */
function Station({ tile, facing, c }: { tile: ProjectTile; facing: 'toward' | 'away'; c: number }) {
  const seat = readSeat(tile);
  const tone = seat.kind === 'contested' ? 'critical' : toneFor('project', tile.status);
  const progressTone =
    tile.status === 'BLOCKED' ? 'critical' : tile.status === 'COMPLETED' ? 'success' : 'active';

  return (
    <article
      className="station"
      data-tone={tone}
      data-project={tile.slug}
      style={{ '--c': c, '--accent': tile.accentColor ?? undefined } as React.CSSProperties}
    >
      <div
        className="station__seat"
        data-occupied={seat.figure}
        data-seat={seat.kind}
        data-tone={seat.tone}
        data-contested={seat.kind === 'contested'}
      >
        <Chair facing={facing} occupied={seat.figure} />
        <SeatLabel seat={seat} />
      </div>

      {/* The console the seat is at. Angled toward the table, so the chair and
          its surface read as one position rather than as a card with a chair
          drawn near it. */}
      <div className="station__console">
        <div className="station__head">
          <h3 className="station__name">
            {/* The one link, stretched over the console by CSS. */}
            <Link className="station__open" to={`/projects/${tile.id}/control`}>
              {tile.name}
            </Link>
          </h3>
          <StateChip kind="project" state={tile.status} />
        </div>

        <div className="station__detail">
          <div className="station__row">
            <span className="dim">Journey</span>
            <span className="mono">
              {tile.journeyCompletionPercent}% · {tile.completedNodes}/{tile.totalNodes}
            </span>
          </div>
          <Meter
            value={tile.journeyCompletionPercent}
            tone={progressTone}
            ariaLabel={`${tile.name} journey completion`}
          />

          <div className="station__row">
            <span className="dim">Now</span>
            <span className="truncate">{tile.currentPositionLabel ?? 'nothing in motion'}</span>
          </div>
          <div className="station__row">
            <span className="dim">Next testable</span>
            <span className="truncate">{tile.nextStationLabel ?? 'none scheduled'}</span>
          </div>

          <div className="station__flags">
            {tile.openBlockers > 0 && (
              <span className="chip" data-tone="critical">
                {tile.openBlockers} blocker{tile.openBlockers === 1 ? '' : 's'}
              </span>
            )}
            {tile.openDecisions > 0 && (
              <span className="chip" data-tone="risk">
                {tile.openDecisions} decision{tile.openDecisions === 1 ? '' : 's'}
              </span>
            )}
            <FreshnessTag freshness={tile.freshness} prefix="source" />
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * A place with no project at it.
 *
 * Furniture, and labelled as furniture. It is not a seat open on a project —
 * that would imply a project — and it carries no workstream, because there
 * isn't one. It exists so the table is a table.
 */
function EmptyPlace({ facing, c }: { facing: 'toward' | 'away'; c: number }) {
  return (
    <div className="place" style={{ '--c': c } as React.CSSProperties}>
      {/* Not `station__seat`: that class is the census of project seats, every
          one of which is either held or open. A spare place is neither, and
          counting it as one would quietly break that arithmetic. */}
      <div className="place__seat" data-seat="unassigned">
        <Chair facing={facing} occupied={false} />
        <div className="seat__label">
          <span className="seat__headline">no station assigned</span>
          <span className="seat__availability">connect a project to staff it</span>
        </div>
      </div>
    </div>
  );
}

/** One side of the table. */
function Arc({
  side,
  tiles,
  places,
  compact,
}: {
  side: 'far' | 'near';
  tiles: ProjectTile[];
  places: number;
  compact: boolean;
}) {
  const facing = side === 'far' ? 'toward' : 'away';
  // Centred, not left-packed. One project at the left end of a row of empty
  // chairs reads as a project standing apart from the table; the same project
  // in the middle reads as the one station that is staffed.
  //
  // Except on a phone, where the arc is too wide for the screen and has to be
  // swiped. Centring there puts the staffed stations off the edge and two
  // unassigned chairs in front of you, which is the wrong half of the table to
  // open on.
  const offset = compact ? 0 : Math.floor((places - tiles.length) / 2);
  return (
    <div className={`arc arc--${side}`}>
      {Array.from({ length: places }, (_, index) => {
        const c = centreness(index, places);
        const tile = tiles[index - offset];
        return tile ? (
          <Station key={tile.id} tile={tile} facing={facing} c={c} />
        ) : (
          <EmptyPlace key={`${side}-${index}`} facing={facing} c={c} />
        );
      })}
    </div>
  );
}

/** A headline on the viewscreen. The full item stays in the strip below. */
function ScreenAlert({ item }: { item: InterventionItem }) {
  const tone = toneFor('severity', item.severity);
  return (
    <li className="screen__alert" data-tone={tone}>
      <span className="chip" data-tone={tone}>
        <span className="chip__glyph" aria-hidden="true">
          {TONE_GLYPH[tone]}
        </span>
        {item.severity}
      </span>
      <span className="truncate">{item.title}</span>
    </li>
  );
}

export function BridgeRoom({ view, compact = false }: { view: PortfolioView; compact?: boolean }) {
  const fleetTone =
    view.fleetHealth.status === 'INCIDENT'
      ? 'critical'
      : view.fleetHealth.status === 'DEGRADED'
        ? 'attention'
        : 'success';

  // The viewscreen carries the headlines only. Severity order is already the
  // projection's order, so the first few are the most urgent few.
  const headlines = view.interventions.slice(0, 4);

  // Every project sits on the far side, in the projection's own order of
  // operational urgency. Those chairs face you, so their consoles are the
  // legible ones, and one row of consoles reads as one desk where two rows of
  // them read as two lists.
  const far = Math.max(MIN_FAR_PLACES, view.projects.length);
  const places = far + NEAR_PLACES;

  return (
    <section className="room" aria-label="Bridge">
      <div className="room__ceiling" aria-hidden="true">
        <span className="room__lamp" />
        <span className="room__lamp room__lamp--side" />
        <span className="room__lamp room__lamp--side room__lamp--right" />
      </div>

      <div className="room__wall room__wall--left" aria-hidden="true">
        <span className="room__strip" />
      </div>
      <div className="room__wall room__wall--right" aria-hidden="true">
        <span className="room__strip" />
      </div>

      <div className="room__viewscreen">
        <div className="screen__header">
          {/* Not the tenant name: the strip directly above the room already
              carries it, and repeating it here says nothing new. What the wall
              can say that the strip cannot is why the fleet is in the state it
              is in. */}
          <span className="screen__health">{view.fleetHealth.detail}</span>
          <span className="chip" data-tone={fleetTone}>
            <span className="chip__glyph" aria-hidden="true">
              {TONE_GLYPH[fleetTone]}
            </span>
            {view.fleetHealth.status === 'OPERATIONAL'
              ? 'Systems operational'
              : view.fleetHealth.status === 'DEGRADED'
                ? 'Degraded sources'
                : 'Incident open'}
          </span>
        </div>

        <div className="screen__alerts">
          <div className="screen__alerts-head">
            <span className="label">Needs intervention</span>
            <span className="mono tiny dim">{view.interventions.length} open</span>
          </div>
          {headlines.length === 0 ? (
            <p className="tiny dim">Nothing is waiting on a person.</p>
          ) : (
            <ul className="screen__alert-list">
              {headlines.map((item) => (
                <ScreenAlert key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* The table. The dominant object in the room, and the thing the fleet
          numbers belong on: they are the portfolio's own readout, not a
          message from outside, which is what the wall is for. */}
      <div className="roomtable" aria-labelledby="table-heading">
        <Arc side="far" tiles={view.projects} places={far} compact={compact} />

        <div className="table">
          <div className="table__surface">
            <div className="table__head">
              <h2 id="table-heading" className="table__title">
                Command table
              </h2>
              <span className="table__sub">
                {view.projects.length === 0
                  ? 'No project has a station yet'
                  : `${view.projects.length} station${view.projects.length === 1 ? '' : 's'} of ${places} places · ordered by operational urgency`}
              </span>
            </div>

            <div className="table__readouts">
              {/* The primary KPI leads, because burn against delivered work is
                  the number this whole system exists to make visible. */}
              <div className="table__readout table__readout--lead">
                <span className="table__value">
                  {view.fleet.burnPerCompletedJourney === null ? (
                    <Unknown />
                  ) : (
                    `${view.fleet.burnPerCompletedJourney}%`
                  )}
                </span>
                <span className="table__label">Burn per completed journey</span>
              </div>
              <div className="table__readout">
                <span className="table__value">{view.fleet.completedJourneys}</span>
                <span className="table__label">Completed journeys · {view.fleet.windowHours}h</span>
              </div>
              <div className="table__readout">
                <span className="table__value">
                  {view.fleet.burnPercent === null ? <Unknown /> : `${view.fleet.burnPercent}%`}
                </span>
                <span className="table__label">Weekly fuel burned</span>
              </div>
              <div className="table__readout">
                <span className="table__value">{view.fleet.activeWorkers}</span>
                <span className="table__label">Active workers · a control input</span>
              </div>
            </div>

            <div className="table__foot">
              <FreshnessTag freshness={view.fleet.freshness} />
            </div>
          </div>
          <div className="table__base" aria-hidden="true" />
        </div>

        <Arc side="near" tiles={[]} places={NEAR_PLACES} compact={compact} />
      </div>

      <div className="room__floor" aria-hidden="true" />
    </section>
  );
}
